"use client";

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { Button } from "@/components/ui/button";
import { Credenza, CredenzaClose, CredenzaContent, CredenzaDescription, CredenzaFooter, CredenzaHeader, CredenzaTitle } from "@/components/ui/credenza";
import { Form } from "@/components/ui/form";
import { FormSelect } from "@/components/ui/select";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useDataProviders } from "@/hooks/useDataProviders";
import { emitter, type SettingsDataProviderEmitterState } from "@/lib/emitter";
import { logger } from "@/lib/services/logger";
import { queryFetcher } from "@/queries/config/query-fetcher";
type GoToAccountChoice = {
  accountKey: string;
  accountName: string;
};
type FinishGoToConnectSetupForm = {
  accountKey: string;
};
const finishGoToConnectSetupSchema = z.object({
  accountKey: z.string().min(1, {
    error: "Account selection is required"
  })
});
export function controlFinishGoToConnectSetup(properties: SettingsDataProviderEmitterState) {
  emitter.emit("settings.workspace.data_provider.finish_setup", properties);
}
export function FinishGoToConnectSetup() {
  const {
    workspaceId
  } = useCurrentWorkspace();
  const {
    dataProviders,
    refreshProviders
  } = useDataProviders();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [isOpen, setOpenState] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedProvider = useMemo(() => {
    return dataProviders.find(provider => provider.id === providerId);
  }, [dataProviders, providerId]);
  const accountChoices = useMemo(() => {
    const credentials = selectedProvider?.credentials?.[0]?.credentials as {
      accountChoices?: GoToAccountChoice[];
    } | undefined;
    return Array.isArray(credentials?.accountChoices) ? credentials.accountChoices.filter(choice => typeof choice?.accountKey === "string" && choice.accountKey.length > 0 && typeof choice?.accountName === "string" && choice.accountName.length > 0).map(choice_0 => ({
      accountKey: choice_0.accountKey,
      accountName: choice_0.accountName
    })) : [];
  }, [selectedProvider]);
  const form = useForm<FinishGoToConnectSetupForm>({
    defaultValues: {
      accountKey: ""
    },
    resolver: zodResolver(finishGoToConnectSetupSchema)
  });
  useEffect(() => {
    const handleFinishSetupEvent = ({
      data,
      state
    }: SettingsDataProviderEmitterState) => {
      setOpenState(state === "on");
      setProviderId(data.providerId);
    };
    emitter.on("settings.workspace.data_provider.finish_setup", handleFinishSetupEvent);
    return () => {
      emitter.off("settings.workspace.data_provider.finish_setup", handleFinishSetupEvent);
    };
  }, []);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    form.reset({
      accountKey: accountChoices[0]?.accountKey ?? ""
    });
  }, [accountChoices, form, isOpen]);
  const handleClose = () => {
    setProviderId(null);
    setOpenState(false);
    controlFinishGoToConnectSetup({
      data: {
        providerId: null
      },
      state: "off"
    });
  };
  const handleSubmit = async (values: FinishGoToConnectSetupForm) => {
    if (!workspaceId || !providerId) {
      return;
    }
    setIsSubmitting(true);
    try {
      await queryFetcher(`/api/workspaces/${workspaceId}/data-provider/goto`, {
        body: JSON.stringify({
          accountKey: values.accountKey
        }),
        method: "POST"
      });
      await refreshProviders();
      handleClose();
    } catch (error) {
      logger.error("Failed to finish GoTo Connect setup", error instanceof Error ? error : new Error(String(error)), {
        providerId,
        workspaceId
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (accountChoices.length === 0) {
    return null;
  }
  return <Credenza onOpenChange={open => {
    if (!open) {
      handleClose();
    }
  }} open={isOpen}>
			<CredenzaContent>
				<CredenzaHeader>
					<CredenzaTitle>Finish GoTo Connect setup</CredenzaTitle>
					<CredenzaDescription>
						Choose the account to finish linking to your workspace.
					</CredenzaDescription>
				</CredenzaHeader>
				<Form {...form}>
					<form className="flex flex-col gap-4 px-4 sm:px-0" id="finish-goto-connect-setup-form" onSubmit={form.handleSubmit(handleSubmit)}>
						<FormSelect form={form} label="GoTo account" name="accountKey" options={accountChoices.map(choice_1 => ({
            label: choice_1.accountName,
            value: choice_1.accountKey
          }))} placeholder="Select an account" triggerProps={{
            className: "w-full"
          }} />
					</form>
				</Form>
				<CredenzaFooter>
					<CredenzaClose asChild>
						<Button disabled={isSubmitting} variant="outline">
							Cancel
						</Button>
					</CredenzaClose>
					<Button disabled={isSubmitting || accountChoices.length === 0} form="finish-goto-connect-setup-form" type="submit">
						{isSubmitting ? "Finishing..." : "Finish setup"}
					</Button>
				</CredenzaFooter>
			</CredenzaContent>
		</Credenza>;
}